/**
 * App-root mount point for the in-app call dialog. Reads from
 * `useCall()` (provided by `<CallProvider>` higher up) and forwards
 * the active-call state to `<InAppCallDialog>`.
 *
 * This component exists so `main.tsx` (or `App.tsx`) can mount one
 * line — `<AppCallDialog />` — without the rest of the App needing to
 * know about the call context's shape.
 */

import { ArrowSquareOut, X } from "@/components/ui/icons";
import { Button } from "@/components/ui/button";
import { isElectronRuntime } from "@/lib/logout";
import { InAppCallDialog } from "./InAppCallDialog";
import { useCall } from "./CallProvider";

export function AppCallDialog() {
  const {
    callDialogOpen,
    endCall,
    activeCall,
    nativeCallWindowOpen,
    popOutCall,
    closeNativeCallWindow,
    handleInlineDisconnected,
  } = useCall();

  if (isElectronRuntime() && nativeCallWindowOpen && activeCall) {
    return (
      <div className="fixed inset-x-0 bottom-6 z-50 flex justify-center px-4">
        <div className="w-full max-w-md rounded-xl border border-border bg-background p-4 shadow-lg">
          <div className="flex items-start justify-between gap-3">
            <div className="space-y-1">
              <p className="text-sm font-medium text-foreground">
                Call is open in a separate window
              </p>
              <p className="text-sm text-muted-foreground">
                #{activeCall.channelName || "channel"}
              </p>
            </div>
            <Button
              variant="ghost"
              size="icon"
              className="shrink-0 text-muted-foreground hover:text-foreground"
              onClick={() => void closeNativeCallWindow()}
              title="Close call window"
            >
              <X size={14} />
            </Button>
          </div>
          <div className="mt-4 flex flex-wrap gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => void popOutCall()}
            >
              <ArrowSquareOut size={14} />
              Bring window to front
            </Button>
            <Button
              variant="destructive"
              size="sm"
              onClick={() => void closeNativeCallWindow()}
            >
              End call
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <InAppCallDialog
      open={callDialogOpen}
      onOpenChange={(next) => {
        if (!next) endCall();
      }}
      onDisconnected={handleInlineDisconnected}
      onPopOut={isElectronRuntime() ? popOutCall : undefined}
      serverUrl={activeCall?.serverUrl ?? null}
      token={activeCall?.token ?? null}
      joinUrl={activeCall?.joinUrl ?? null}
      mode={activeCall?.mode ?? null}
      channelName={activeCall?.channelName ?? "channel"}
    />
  );
}
